/* eslint-disable eslint-comments/disable-enable-pair */

/* eslint-disable max-lines */
import { LoadingSpinner, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import type { HiringFeedsResponse } from "@src/appV2/redesign/HiringFeed/api/hiringFeed.schema";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { Fragment } from "react";
import { InView } from "react-intersection-observer";

import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import { transformResponseIntoHiringFeed } from "../../HiringFeed/api/usePaginatedHiringFeed";
import { augmentYearsOfExperienceWithWorkerLicenses } from "../../PlacementCandidate/utils";
import { useGetIncentives } from "../api/useGetIncentives";
import { useHiringInstantPay } from "../hooks/useHiringInstantPay";
import { usePlacementRecruiterScreeningConfig } from "../hooks/usePlacementRecruiterScreeningConfig";
import { usePayTransparencyRates } from "../lib/usePayTransparencyRates";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { useHiringIncentiveProgram } from "../useHiringIncentiveProgram";
import { hiringFeedToPlacement } from "../utils/placement-data";
import { FiltersTopBar } from "./FiltersTopBar";
import { InstantPayHiringBanner } from "./InstantPayHiringBanner";
import { PlacementCard } from "./PlacementCard";
import { PlacementsLoader } from "./PlacementsLoader";
import { RecruiterScreeningCard } from "./RecruiterScreeningCall/RecruiterScreeningCard";
import { SignOnBonusBanner } from "./SignOnBonusBanner";

interface OpenPlacementListViewProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  showApplications: boolean;
  hiringFeedResults: UseInfiniteQueryResult<HiringFeedsResponse>;
}

export function OpenPlacementListView(props: OpenPlacementListViewProps) {
  const { placementCandidate, worker, filter, setFilter, showApplications, hiringFeedResults } =
    props;
  const { hiringIncentiveProgram } = useHiringIncentiveProgram();

  const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
    placementCandidate.yearsOfExperienceByQualification,
    worker.licenses
  );

  const {
    data: hiringFeedData,
    isSuccess: isHiringFeedSuccess,
    hasNextPage: hasNextPageHiringFeed,
    fetchNextPage: fetchNextPageHiringFeed,
    isFetchingNextPage: isFetchingNextPageHiringFeed,
  } = hiringFeedResults;

  const { data: signOnBonuses, isSuccess: isGetIncentivesSuccess } = useGetIncentives(
    {
      workerId: worker.userId,
      queryParams: {
        filter: { sourceType: "Placement" },
      },
    },
    { enabled: hiringIncentiveProgram.enabled }
  );

  const { getCachedPayRatesForPlacement, isLoading: isPayDataLoading } = usePayTransparencyRates();

  const isSignOnBonusPaid = signOnBonuses?.data.some(
    (incentive) => incentive.attributes.status === "paid"
  );
  const { showScreeningCard } = usePlacementRecruiterScreeningConfig();
  const hiringFeeds = hiringFeedData?.pages.flatMap((page) => page.data);

  const { enabled: isHiringInstantPayEnabled } = useHiringInstantPay();

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  // Get available states from API response meta (for state filter)
  const availableStates = hiringFeedData?.pages[0].meta?.availableStates ?? [];
  const shouldShowStateFilter = availableStates.length > 1;

  return (
    <>
      {/* Sticky Filters Top Bar - rendered here to keep it local to the list and reliably sticky */}
      {!showApplications && isHiringFeedSuccess && (
        <FiltersTopBar
          filter={filter}
          setFilter={setFilter}
          worker={worker}
          placementCandidateId={placementCandidate.id}
          availableStates={availableStates}
          shouldShowStateFilter={shouldShowStateFilter}
        />
      )}
      {isEmpty(hiringFeedData) && <PlacementsLoader />}

      {isHiringFeedSuccess && (hiringFeedData?.pages[0]?.data.length ?? 0) === 0 && (
        <Stack justifyContent="center" flex={1} alignItems="center" sx={{ pt: 5 }}>
          <Text variant="body1">There are no relevant jobs for you yet! </Text>
        </Stack>
      )}

      {isDefined(hiringFeedData) && hiringFeedData.pages.length > 0 && (
        <>
          {hiringFeeds?.some(
            (placement) => !placement.meta.matchedType || placement.meta.matchedType === "exact"
          ) ? (
            <Stack direction="column" spacing={6} pt={1}>
              {hiringFeedData?.pages.flatMap((hiringFeedPage, pageIndex) => {
                let globalIndex = 0;
                for (let index = 0; index < pageIndex; index += 1) {
                  globalIndex += hiringFeedData.pages[index].data.length;
                }

                return hiringFeedPage.data.map((hiringFeedItem, placementIndex) => {
                  const currentGlobalIndex = globalIndex + placementIndex;
                  const hiringFeedWithDetails = transformResponseIntoHiringFeed(
                    hiringFeedItem,
                    hiringFeedPage.included,
                    credentialRequirementConfiguration
                  );

                  const hiringEventId = hiringFeedItem.relationships.hiringEvents?.data.at(0)?.id;
                  const itemKey = `${hiringFeedItem.id}-${hiringEventId ?? ""}`;

                  const shouldShowRecruiterScreeningCard =
                    showScreeningCard && currentGlobalIndex === 0;

                  const shouldShowInstantPayHiringBanner =
                    isHiringInstantPayEnabled && currentGlobalIndex === 0;

                  const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                    {
                      id: hiringFeedWithDetails.id,
                      workplaceId: hiringFeedWithDetails.workplace.id,
                      workerTypes: hiringFeedWithDetails.workerTypes,
                      jobShifts: hiringFeedWithDetails.jobShifts,
                      transparencyPayRates: hiringFeedWithDetails.transparencyPayRates,
                      attributes: { payRange: hiringFeedWithDetails.payRange },
                    },
                    yearsOfExperienceByQualification,
                    false,
                    placementCandidate.payAndScheduleFormData?.benefits?.combined
                  );

                  return !hiringFeedItem.meta.matchedType ||
                    hiringFeedItem.meta.matchedType === "exact" ||
                    hiringFeedItem.meta.matchedType === "recentlyAdded" ? (
                    <Fragment key={itemKey}>
                      {currentGlobalIndex === 2 &&
                        hiringIncentiveProgram.enabled &&
                        !isSignOnBonusPaid &&
                        !placementCandidate.isRestricted &&
                        isGetIncentivesSuccess && (
                          <SignOnBonusBanner
                            placementId={hiringFeedWithDetails.id}
                            placementCandidate={placementCandidate}
                          />
                        )}
                      {shouldShowRecruiterScreeningCard && (
                        <RecruiterScreeningCard placementCandidate={placementCandidate} />
                      )}
                      {shouldShowInstantPayHiringBanner && <InstantPayHiringBanner />}
                      <PlacementCard
                        key={itemKey}
                        placement={hiringFeedToPlacement(hiringFeedWithDetails)}
                        placementCandidateId={placementCandidate.id}
                        isRestricted={placementCandidate.isRestricted ?? false}
                        pageIndex={pageIndex}
                        placementIndex={placementIndex}
                        loadingPayData={isPayDataLoading}
                        minMaxPayEstimate={minMaxPayEstimate}
                        source="placement_card"
                      />
                    </Fragment>
                  ) : undefined;
                });
              })}
            </Stack>
          ) : undefined}
          {hiringFeeds?.some((placement) => placement.meta.matchedType === "discovery") ? (
            <Box>
              <Box>
                <Stack sx={{ textAlign: "center", marginY: 6, marginBottom: 8 }} spacing={1}>
                  {hiringFeeds?.filter((placement) => placement.meta.matchedType === "exact")
                    .length === 0 ? (
                    <>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        No jobs match your filters.
                      </Text>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        Update your filters or discover other jobs below.
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        You&apos;ve reached the end of your filtered matches.
                      </Text>
                      <Text sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        Update filters or explore more jobs below!
                      </Text>
                    </>
                  )}
                </Stack>
              </Box>
              <Stack
                direction="column"
                spacing={8}
                sx={{
                  background: (theme) => theme.palette.background.quaternary,
                  marginX: -5,
                  paddingX: 5,
                  paddingY: 8,
                }}
              >
                <Text variant="h4">Explore other jobs you might like</Text>
                <Stack direction="column" spacing={6}>
                  {hiringFeedData?.pages.flatMap((hiringFeedPage, pageIndex) => {
                    return hiringFeedPage.data.map((hiringFeedItem, placementIndex) => {
                      const hiringFeedWithDetails = transformResponseIntoHiringFeed(
                        hiringFeedItem,
                        hiringFeedPage.included,
                        credentialRequirementConfiguration
                      );

                      const { minMaxPayEstimate } = getCachedPayRatesForPlacement(
                        {
                          id: hiringFeedWithDetails.id,
                          workplaceId: hiringFeedWithDetails.workplace.id,
                          workerTypes: hiringFeedWithDetails.workerTypes,
                          jobShifts: hiringFeedWithDetails.jobShifts,
                          transparencyPayRates: hiringFeedWithDetails.transparencyPayRates,
                          attributes: { payRange: hiringFeedWithDetails.payRange },
                        },
                        yearsOfExperienceByQualification,
                        false,
                        placementCandidate.payAndScheduleFormData?.benefits?.combined
                      );

                      return hiringFeedItem.meta.matchedType === "discovery" ? (
                        <Fragment key={hiringFeedItem.id}>
                          <PlacementCard
                            placement={hiringFeedToPlacement(hiringFeedWithDetails)}
                            placementCandidateId={placementCandidate.id}
                            isRestricted={placementCandidate.isRestricted ?? false}
                            pageIndex={pageIndex}
                            placementIndex={placementIndex}
                            loadingPayData={isPayDataLoading}
                            minMaxPayEstimate={minMaxPayEstimate}
                            source="placement_card"
                          />
                        </Fragment>
                      ) : undefined;
                    });
                  })}
                </Stack>
              </Stack>
            </Box>
          ) : undefined}
        </>
      )}

      {hasNextPageHiringFeed && !placementCandidate.isRestricted && (
        <Stack direction="row" justifyContent="center">
          <Box sx={{ marginTop: 4, marginBottom: 10 }}>
            <InView
              as="div"
              onChange={async (inView) => {
                if (inView && !isFetchingNextPageHiringFeed) {
                  await fetchNextPageHiringFeed();
                  logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                    placementCandidateId: placementCandidate.id,
                    filters: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
                    action: "load_more_clicked",
                  });
                }
              }}
            />
            <Box sx={{ opacity: isFetchingNextPageHiringFeed ? 1 : 0 }}>
              <LoadingSpinner size="medium" />
            </Box>
          </Box>
        </Stack>
      )}
    </>
  );
}
